/**
 * Pulse content.yml validator — enforces Pulse component Zod schemas against
 * generated `pages/**\/content.yml` files at artifact-write time.
 *
 * This breaks the swp-2jfr / swp-fcy3 whack-a-mole pattern where otters emit
 * adjacent-but-wrong prop names (valueField, label, renderAs, severityMap) that
 * only fail at runtime. The validator runs the actual Zod schemas from
 * @stackwright-pro/pulse and produces an actionable retryPrompt naming the
 * exact wrong prop + the right prop + a literal copy-paste swap suggestion.
 */
import { load as yamlLoad } from 'js-yaml';
import { readFileSync, readdirSync, statSync, existsSync } from 'fs';
import { join, relative } from 'path';
import { z } from 'zod';
import { PULSE_CONTENT_SCHEMAS, ColumnSchema } from '@stackwright-pro/pulse';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface PulseViolationIssue {
  path: string[];
  message: string;
}

export interface PulseContentViolation {
  /** Path relative to projectRoot, e.g. "pages/dashboard/content.yml" */
  file: string;
  /** jq-style YAML path to the item, e.g. "content.content_items[2]" */
  yamlPath: string;
  /** Content type key, e.g. "metric_card_pulse" */
  contentType: string;
  /** Zod validation issues */
  issues: PulseViolationIssue[];
  /** Top-level keys actually present in the YAML object (minus 'type') */
  emittedProps: string[];
  /** Top-level keys allowed by the schema */
  allowedProps: string[];
  /** Raw YAML item — used by the formatter for sub-object introspection (e.g. columns) */
  rawItem: Record<string, unknown>;
}

export interface ValidatePulseContentResult {
  valid: boolean;
  filesScanned: number;
  itemsChecked: number;
  violations: PulseContentViolation[];
}

// ---------------------------------------------------------------------------
// Schema introspection helpers
// ---------------------------------------------------------------------------

/**
 * Derive the top-level allowed prop names from a Zod schema.
 * Duck-types for `.shape` to avoid the instanceof dual-instance trap that
 * bites monorepos with per-package node_modules (Zod v4 included).
 */
function getAllowedProps(schema: z.ZodTypeAny): string[] {
  // Walk down optional/nullable wrappers

  let inner: any = schema;
  while (inner && typeof inner.unwrap === 'function') {
    inner = inner.unwrap();
  }
  // Duck-type: if it has a `.shape` that is a plain object, extract keys
  if (inner && typeof inner === 'object' && inner.shape && typeof inner.shape === 'object') {
    return Object.keys(inner.shape as object);
  }
  return [];
}

/** Column-level allowed props — used by formatter for data_table_pulse. */
const COLUMN_ALLOWED_PROPS = getAllowedProps(ColumnSchema);

// ---------------------------------------------------------------------------
// Recursive YAML walker
// ---------------------------------------------------------------------------

/**
 * Walk a YAML node with explicit key-path tracking.
 * Validates any object whose `type` key matches a key in PULSE_CONTENT_SCHEMAS.
 * Always continues recursing — content_items can nest arbitrarily deep.
 */
function walk(
  node: unknown,
  path: string,
  relativeFilePath: string,
  violations: PulseContentViolation[],
  counter: { checked: number }
): void {
  if (node === null || node === undefined || typeof node !== 'object') return;

  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      walk(node[i], `${path}[${i}]`, relativeFilePath, violations, counter);
    }
    return;
  }

  const obj = node as Record<string, unknown>;

  // Validate if this is a registered Pulse content type
  const contentType = typeof obj['type'] === 'string' ? obj['type'] : null;
  if (contentType !== null && contentType in PULSE_CONTENT_SCHEMAS) {
    counter.checked++;
    const schema = PULSE_CONTENT_SCHEMAS[contentType]!;
    const result = schema.safeParse(obj);

    if (!result.success) {
      violations.push({
        file: relativeFilePath,
        yamlPath: path,
        contentType,
        issues: result.error.issues.map((issue) => ({
          path: issue.path.map(String),
          message: issue.message,
        })),
        emittedProps: Object.keys(obj).filter((k) => k !== 'type'),
        allowedProps: getAllowedProps(schema),
        rawItem: obj,
      });
    }
  }

  // Always recurse into every child — content can nest arbitrarily
  for (const [key, child] of Object.entries(obj)) {
    walk(child, `${path}.${key}`, relativeFilePath, violations, counter);
  }
}

// ---------------------------------------------------------------------------
// File discovery
// ---------------------------------------------------------------------------

/**
 * Find all `content.yml` files under `dir`, recursively.
 */
function findContentYamlFiles(dir: string): string[] {
  const results: string[] = [];
  if (!existsSync(dir)) return results;

  for (const entry of readdirSync(dir)) {
    const fullPath = join(dir, entry);
    const stat = statSync(fullPath);
    if (stat.isDirectory()) {
      results.push(...findContentYamlFiles(fullPath));
    } else if (entry === 'content.yml') {
      results.push(fullPath);
    }
  }
  return results;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Walk every `pages/**\/content.yml` under `projectRoot`, parse YAML, and
 * validate any Pulse content_item whose `type` is registered in
 * `PULSE_CONTENT_SCHEMAS`.
 *
 * Unknown content types pass through unchecked (YAGNI — don't enforce what
 * we don't have schemas for).
 */
export function validatePulseContent(projectRoot: string): ValidatePulseContentResult {
  const pagesDir = join(projectRoot, 'pages');
  const contentFiles = findContentYamlFiles(pagesDir);

  const violations: PulseContentViolation[] = [];
  const counter = { checked: 0 };

  for (const absolutePath of contentFiles) {
    const relPath = relative(projectRoot, absolutePath);
    let parsed: unknown;
    try {
      const raw = readFileSync(absolutePath, 'utf-8');
      parsed = yamlLoad(raw);
    } catch {
      // Malformed YAML — not our job here, let the otter's schema validator catch it
      continue;
    }

    walk(parsed, 'content', relPath, violations, counter);
  }

  return {
    valid: violations.length === 0,
    filesScanned: contentFiles.length,
    itemsChecked: counter.checked,
    violations,
  };
}

// ---------------------------------------------------------------------------
// retryPrompt formatting
// ---------------------------------------------------------------------------

/**
 * Derive human-readable "unknown props the otter used" from a violation.
 * These are props that appear in the YAML but not in the schema.
 */
function unknownProps(violation: PulseContentViolation): string[] {
  const allowed = new Set(violation.allowedProps);
  return violation.emittedProps.filter((p) => !allowed.has(p));
}

/**
 * For a data_table_pulse violation, find column-level issues and return
 * a per-column breakdown.
 */
interface ColumnViolationSummary {
  index: number;
  emittedProps: string[];
  unknownProps: string[];
  missingProps: string[];
}

function extractColumnViolations(violation: PulseContentViolation): ColumnViolationSummary[] {
  if (violation.contentType !== 'data_table_pulse') return [];

  // Group issues by column index
  const byColumn = new Map<number, string[]>();
  for (const issue of violation.issues) {
    if (issue.path[0] === 'columns' && typeof issue.path[1] === 'string') {
      const colIdx = parseInt(issue.path[1], 10);
      if (!isNaN(colIdx)) {
        const missing = issue.path[2] ?? issue.message;
        if (!byColumn.has(colIdx)) byColumn.set(colIdx, []);
        byColumn.get(colIdx)!.push(String(missing));
      }
    }
  }

  if (byColumn.size === 0) return [];

  const rawColumns = Array.isArray(violation.rawItem['columns'])
    ? (violation.rawItem['columns'] as Record<string, unknown>[])
    : [];
  const columnAllowed = new Set(COLUMN_ALLOWED_PROPS);

  const summaries: ColumnViolationSummary[] = [];
  for (const [colIdx, missingProps] of byColumn.entries()) {
    const rawCol = rawColumns[colIdx] ?? {};
    const emitted = Object.keys(rawCol);
    const unknown = emitted.filter((p) => !columnAllowed.has(p));
    summaries.push({
      index: colIdx,
      emittedProps: emitted,
      unknownProps: unknown,
      missingProps: [...new Set(missingProps)],
    });
  }

  return summaries.sort((a, b) => a.index - b.index);
}

/**
 * Generate a one-line swap suggestion when the mismatch is unambiguous:
 * exactly one unknown prop + exactly one missing required prop at the same level.
 * Returns null when the situation is ambiguous.
 */
function swapSuggestion(
  unknownPropNames: string[],
  missingPropNames: string[],
  itemType: 'top' | 'column'
): string | null {
  if (unknownPropNames.length !== 1 || missingPropNames.length !== 1) return null;
  const level = itemType === 'column' ? 'column property' : 'property';
  return `  Fix: rename ${level} \`${unknownPropNames[0]}\` → \`${missingPropNames[0]}\``;
}

/**
 * Format a ValidatePulseContentResult into an actionable retryPrompt.
 *
 * Teaching goals:
 * 1. Show the EXACT prop the otter used (unknown to the schema)
 * 2. Show the EXACT prop the schema requires (missing from the YAML)
 * 3. Provide a literal rename suggestion when unambiguous
 * 4. Enumerate the FULL allowed-prop list so the otter sees the boundary
 * 5. Explicitly name adjacent-wrong-names to discourage adjacent guessing
 */
export function formatPulseRetryPrompt(result: ValidatePulseContentResult): string {
  if (result.valid) return '';

  // Group violations by file
  const byFile = new Map<string, PulseContentViolation[]>();
  for (const v of result.violations) {
    if (!byFile.has(v.file)) byFile.set(v.file, []);
    byFile.get(v.file)!.push(v);
  }

  const fileCount = byFile.size;
  const totalViolations = result.violations.length;
  const lines: string[] = [
    `Pulse schema validation failed: ${totalViolations} violation(s) across ${fileCount} content.yml file(s).`,
    '',
    'You MUST fix ALL violations and re-call stackwright_pro_validate_artifact.',
    'Do NOT invent new prop names. Only use props listed in "Schema allows".',
    '',
  ];

  for (const [file, violations] of byFile.entries()) {
    lines.push(`${file} — ${violations.length} violation(s):`);

    for (const v of violations) {
      lines.push(`  ${v.yamlPath} (${v.contentType}):`);

      const unknown = unknownProps(v);
      const colViolations = extractColumnViolations(v);

      if (colViolations.length > 0) {
        // data_table_pulse with column-level issues
        for (const col of colViolations) {
          lines.push(`    columns[${col.index}]:`);
          if (col.unknownProps.length > 0) {
            lines.push(
              `      Unknown prop(s) used: ${col.unknownProps.map((p) => `\`${p}\``).join(', ')}`
            );
          }
          if (col.missingProps.length > 0) {
            lines.push(
              `      Missing required prop(s): ${col.missingProps.map((p) => `\`${p}\``).join(', ')}`
            );
          }
          const suggestion = swapSuggestion(col.unknownProps, col.missingProps, 'column');
          if (suggestion) lines.push(suggestion);
        }
        lines.push(
          `    Schema allows ONLY these column props: ${COLUMN_ALLOWED_PROPS.join(', ')}.`
        );
        lines.push(
          `    Do NOT use: \`label\` (use \`header\`), \`renderAs\` (use \`type\`), \`severityMap\` (use \`colorMap\`), \`width\` (not supported).`
        );
      } else if (unknown.length > 0 || v.issues.length > 0) {
        // Top-level prop violations
        const missingFromIssues = v.issues
          .filter((i) => i.path.length === 1 && !i.path[0]!.match(/^\d+$/))
          .map((i) => i.path[0]!)
          .filter(Boolean);

        if (unknown.length > 0) {
          lines.push(`    Unknown prop(s) used: ${unknown.map((p) => `\`${p}\``).join(', ')}`);
        }
        if (missingFromIssues.length > 0) {
          lines.push(
            `    Missing required prop(s): ${[...new Set(missingFromIssues)].map((p) => `\`${p}\``).join(', ')}`
          );
        }
        const suggestion = swapSuggestion(unknown, [...new Set(missingFromIssues)], 'top');
        if (suggestion) lines.push(suggestion);

        lines.push(`    Schema allows ONLY these props: ${v.allowedProps.join(', ')}.`);

        if (v.contentType === 'metric_card_pulse') {
          lines.push(
            `    Do NOT use: \`valueField\`, \`valuePath\`, \`dataField\`, \`value\`. The correct prop is \`field\` (dot-path into the collection).`
          );
        }
      } else {
        // Other issues (e.g. enum mismatches, type errors at non-column paths)
        for (const issue of v.issues) {
          lines.push(`    ${issue.path.join('.')}: ${issue.message}`);
        }
        lines.push(`    Schema allows ONLY these props: ${v.allowedProps.join(', ')}.`);
      }
    }
    lines.push('');
  }

  lines.push(
    'After fixing ALL violations above, re-call stackwright_pro_validate_artifact with the corrected artifact.'
  );

  return lines.join('\n');
}
